export type FinanceAPI = <T>(path: string, options?: RequestInit) => Promise<T>;

type FinanceOverview = {
  collectedAtomic: string;
  customerPrepaymentAtomic: string;
  platformFeeAtomic: string;
  providerPayableAtomic: string;
  providerPaidAtomic: string;
  pendingPayouts: number;
  unknownPayouts: number;
  usageCount: number;
};

type CollectionSettings = {
  id: string;
  version: number;
  enabled: boolean;
  network: string;
  tokenAddress: string;
  receiverWallet: string;
  feeModel: string;
  createdBy: string;
  createdAt: string;
};

type FeeRule = {
  id: string;
  scope: "DEFAULT" | "PROVIDER";
  providerId?: string;
  feeBps: number;
  version: number;
};

type FeeProvider = {
  id: string;
  name: string;
  slug: string;
  status: string;
};

type FeeRuleOverview = {
  defaultRule: FeeRule;
  providerRules: FeeRule[];
  providers: FeeProvider[];
};

type UsageEvent = {
  id: string;
  orderId: string;
  paymentMode: string;
  buyerGrossAmountAtomic: string;
  platformFeeAmountAtomic: string;
  providerPayableAmountAtomic: string;
  fundingState: string;
  createdAt: string;
};

type ProviderPayable = {
  id: string;
  usageEventId: string;
  providerRef: string;
  providerWallet: string;
  amountAtomic: string;
  paidAmountAtomic: string;
  fundingState: string;
  status: string;
};

type Payout = {
  id: string;
  providerPayableId: string;
  status: string;
  amountAtomic: string;
  transactionHash?: string;
  lastErrorCode?: string;
  providerWallet?: string;
  createdAt: string;
};

type Reconciliation = {
  settledOrdersMissingUsage: number;
  usageMissingLedger: number;
  usageMissingPayable: number;
  payableMissingPayout: number;
  paidPayoutMissingTransaction: number;
};

type PrepaidFinanceOverview = {
  customerCount: number;
  providerCount: number;
  rechargeCount: number;
  rechargeTotalAtomic: string;
  availableBalanceAtomic: string;
  consumedAmountAtomic: string;
  refundedAmountAtomic: string;
  platformFeeRecognizedAtomic: string;
  providerPayableRecognizedAtomic: string;
  providerPaidAtomic: string;
  providerOutstandingAtomic: string;
};

type PrepaidFinanceItem = {
  grantId: string;
  subscriptionId: string;
  productId: string;
  productName: string;
  orderId: string;
  payerWallet: string;
  providerId: string;
  providerName: string;
  providerWallet: string;
  grantStatus: string;
  settlementStatus: "NOT_DUE" | "PENDING" | "PARTIAL" | "SETTLED";
  quantityTotal: number;
  quantityRemaining: number;
  rechargeAmountAtomic: string;
  availableBalanceAtomic: string;
  consumedAmountAtomic: string;
  platformFeeRecognizedAtomic: string;
  providerPayableRecognizedAtomic: string;
  providerPaidAtomic: string;
  providerOutstandingAtomic: string;
  platformFeeBps: number;
  payoutCount: number;
  paidPayoutCount: number;
  pendingPayoutCount: number;
  exceptionPayoutCount: number;
  incomingTransactionHash?: string;
  latestPayoutTransactionHash?: string;
  refundAmountAtomic: string;
  refundStatus?: string;
  refundTransactionHash?: string;
  createdAt: string;
};

type PrepaidFinanceOption = { id: string; name: string };

type PrepaidFinancePagination = {
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
};

type PrepaidFinanceResponse = {
  overview: PrepaidFinanceOverview;
  recharges: PrepaidFinanceItem[];
  pagination: PrepaidFinancePagination;
  products: PrepaidFinanceOption[];
  providers: PrepaidFinanceOption[];
};

type FinanceTab = "overview" | "recharges" | "settlements" | "reconciliation" | "settings";

type Dependencies = {
  api: FinanceAPI;
  formatUSDC: (value: string) => string;
  escapeHTML: (value: string) => string;
  shortAddress: (value: string) => string;
};

export type FinanceViewController = {
  load: () => Promise<void>;
  summary: () => string;
};

const emptyOverview: FinanceOverview = {
  collectedAtomic: "0",
  customerPrepaymentAtomic: "0",
  platformFeeAtomic: "0",
  providerPayableAtomic: "0",
  providerPaidAtomic: "0",
  pendingPayouts: 0,
  unknownPayouts: 0,
  usageCount: 0,
};

const emptyPrepaidOverview: PrepaidFinanceOverview = {
  customerCount: 0,
  providerCount: 0,
  rechargeCount: 0,
  rechargeTotalAtomic: "0",
  availableBalanceAtomic: "0",
  consumedAmountAtomic: "0",
  refundedAmountAtomic: "0",
  platformFeeRecognizedAtomic: "0",
  providerPayableRecognizedAtomic: "0",
  providerPaidAtomic: "0",
  providerOutstandingAtomic: "0",
};

export function installFinanceView(deps: Dependencies): FinanceViewController {
  const nav = document.querySelector(".sidebar nav")!;
  nav.querySelector('[data-view="access"]')!.insertAdjacentHTML("beforebegin", `
    <button class="nav-item" data-view="finance" data-permission="revenue" type="button">
      <span>07</span>财务中心
    </button>
  `);
  document.querySelector("main.workspace")!.insertAdjacentHTML("beforeend", `
    <section id="finance-view" hidden>
      <div class="finance-subnav">
        <nav class="finance-tabs" aria-label="财务中心功能" role="tablist">
          <button class="active" data-finance-tab="overview" type="button" role="tab" aria-selected="true">资金总览</button>
          <button data-finance-tab="recharges" type="button" role="tab" aria-selected="false">客户充值</button>
          <button data-finance-tab="settlements" type="button" role="tab" aria-selected="false">Provider 结算</button>
          <button data-finance-tab="reconciliation" type="button" role="tab" aria-selected="false">账务对账</button>
          <button data-finance-tab="settings" type="button" role="tab" aria-selected="false">资金配置</button>
        </nav>
        <span id="finance-updated-at">尚未刷新</span>
      </div>

      <section class="finance-tab-panel" data-finance-panel="overview" role="tabpanel">
        <div class="notice prepaid-finance-notice">
          <strong>客户充值先进入平台资金池，成功 Usage 后再按冻结费率结算给 Provider。</strong>
          <span>这里展示当前预付资金规模和结算进度，明细与配置分别进入对应页签。</span>
        </div>
        <div class="finance-summary-meta">
          <span><strong id="prepaid-customer-count">0</strong> 位客户</span>
          <span><strong id="prepaid-recharge-count">0</strong> 笔充值</span>
          <span><strong id="prepaid-provider-count">0</strong> 个 Provider</span>
          <span id="prepaid-refund-summary">退款 0 USDC</span>
        </div>
        <div class="order-stats prepaid-finance-stats">
          <article class="revenue-stat"><span>累计充值</span><strong><b id="prepaid-recharge-total">0</b> USDC</strong></article>
          <article><span>当前可用余额</span><strong><b id="prepaid-available">0</b> USDC</strong></article>
          <article><span>已消费</span><strong><b id="prepaid-consumed">0</b> USDC</strong></article>
          <article class="profit-stat"><span>平台收入</span><strong><b id="prepaid-fee">0</b> USDC</strong></article>
          <article><span>Provider 已结</span><strong><b id="prepaid-provider-paid">0</b> USDC</strong></article>
          <article><span>Provider 待结</span><strong><b id="prepaid-provider-outstanding">0</b> USDC</strong></article>
        </div>
        <div class="finance-alert" id="finance-alert" hidden></div>
      </section>

      <section class="finance-tab-panel" data-finance-panel="recharges" role="tabpanel" hidden>
        <div class="finance-panel-head">
          <div><p class="eyebrow">CUSTOMER FUNDS</p><h2>客户充值明细</h2></div>
          <p>一行一笔充值，完整标识与链上交易在“查看详情”中展示。</p>
        </div>
        <form class="order-filters prepaid-finance-filters" id="prepaid-finance-filters">
          <label><span>产品</span><select id="prepaid-product-filter"><option value="">全部产品</option></select></label>
          <label><span>Provider</span><select id="prepaid-provider-filter"><option value="">全部 Provider</option></select></label>
          <label><span>客户钱包</span><input id="prepaid-wallet-filter" placeholder="完整 0x 钱包地址" /></label>
          <label><span>每页</span><select id="prepaid-page-size"><option>20</option><option>50</option><option>100</option></select></label>
          <button type="submit">查询充值</button>
        </form>
        <div class="table-panel finance-table prepaid-finance-table">
          <div class="panel-head"><strong>充值与结算进度</strong><span id="prepaid-recharge-table-summary">共 0 笔</span></div>
          <div class="table-wrap"><table><thead><tr><th>客户</th><th>产品</th><th>充值 / 剩余</th><th>Provider</th><th>应结 / 已结</th><th>状态</th><th>详情</th></tr></thead><tbody id="prepaid-finance-rows"></tbody></table></div>
          <div class="empty-row" id="prepaid-finance-empty">暂无符合条件的充值。</div>
          <div class="order-pagination">
            <button id="prepaid-prev-page" type="button">← 上一页</button>
            <span id="prepaid-page-label">第 1 / 1 页</span>
            <button id="prepaid-next-page" type="button">下一页 →</button>
          </div>
        </div>
      </section>

      <section class="finance-tab-panel" data-finance-panel="settlements" role="tabpanel" hidden>
        <div class="finance-panel-head">
          <div><p class="eyebrow">PROVIDER PAYOUT</p><h2>Provider 结算</h2></div>
          <p>查看应付、已付、出款状态及链上交易，UNKNOWN 状态需先对账。</p>
        </div>
        <div class="table-panel finance-table finance-payout-table">
          <div class="panel-head"><strong>Provider 应付与出款</strong><span>UNKNOWN 必须先对账，不能直接重试</span></div>
          <div class="table-wrap"><table><thead><tr><th>Provider</th><th>应付</th><th>资金 / 应付状态</th><th>出款状态</th><th>链上交易</th><th></th></tr></thead><tbody id="finance-payout-rows"></tbody></table></div>
          <div class="empty-row" id="finance-payout-empty">暂无 Provider 应付。</div>
        </div>
      </section>

      <section class="finance-tab-panel" data-finance-panel="reconciliation" role="tabpanel" hidden>
        <div class="finance-panel-head">
          <div><p class="eyebrow">LEDGER CONTROL</p><h2>账务对账</h2></div>
          <p>核对平台总账、Usage、Provider 应付和链上出款之间的完整性。</p>
        </div>
        <div class="finance-ledger-summary">
          <div><span>平台已收款</span><strong><b id="finance-collected">0</b> USDC</strong></div>
          <div><span>客户预存负债</span><strong><b id="finance-prepayment">0</b> USDC</strong></div>
          <div><span>已确认平台手续费</span><strong><b id="finance-fee">0</b> USDC</strong></div>
          <div><span>Provider 待付</span><strong><b id="finance-payable">0</b> USDC</strong></div>
          <div><span>Provider 已付</span><strong><b id="finance-paid">0</b> USDC</strong></div>
          <div><span>出款异常</span><strong><b id="finance-unknown">0</b> 笔</strong></div>
        </div>
        <div class="table-panel finance-reconciliation">
          <div class="panel-head"><strong>账务完整性检查</strong><button id="finance-refresh" type="button">刷新数据</button></div>
          <div class="reconciliation-list" id="finance-reconciliation-list"></div>
        </div>
        <div class="table-panel finance-table">
          <div class="panel-head"><strong>Usage 计费明细</strong><span>最近 50 条成功履约计费事实</span></div>
          <div class="table-wrap"><table><thead><tr><th>Usage / Order</th><th>收款</th><th>手续费</th><th>Provider 应付</th><th>资金状态</th></tr></thead><tbody id="finance-usage-rows"></tbody></table></div>
          <div class="empty-row" id="finance-usage-empty">暂无 Usage。</div>
        </div>
      </section>

      <section class="finance-tab-panel" data-finance-panel="settings" role="tabpanel" hidden>
        <div class="finance-panel-head">
          <div><p class="eyebrow">CONTROLLED SETTINGS</p><h2>资金配置</h2></div>
          <p>配置会影响新报价和后续资金流，历史报价与已产生账务不会被改写。</p>
        </div>
        <div class="finance-settings-grid">
          <form class="editor-panel finance-settings" id="finance-settings-form">
            <div class="panel-head"><strong>平台收款配置</strong><span id="finance-config-state">—</span></div>
            <label class="toggle-field"><input id="finance-enabled" type="checkbox" />启用此收款配置版本</label>
            <label class="field"><span>网络</span><input id="finance-network" value="eip155:84532" required /></label>
            <label class="field"><span>Token 地址</span><input id="finance-token" required /></label>
            <label class="field wallet-field"><span>平台统一收款 Receiver</span><input id="finance-receiver" required /></label>
            <div class="credential-note"><strong>平台费内扣</strong><p>用户只支付 Provider 定义的售价；平台手续费从售价内扣，Product Receiver 用作 Provider 出款钱包。</p></div>
            <div class="editor-actions"><button class="primary-button" type="submit"><span>创建新版本</span><b>→</b></button></div>
            <p class="form-status" id="finance-settings-status" hidden></p>
          </form>

          <form class="table-panel editor-panel finance-fee-settings" id="finance-fee-form">
            <div class="panel-head"><strong>平台手续费规则</strong><span>100 BPS = 1%</span></div>
            <div class="finance-fee-grid">
              <div>
                <label class="field"><span>全局默认费率（BPS）</span><input id="finance-default-bps" type="number" min="0" max="10000" step="1" required /></label>
                <p class="field-help" id="finance-default-bps-preview">—</p>
              </div>
              <div>
                <label class="field"><span>服务商单独费率</span><select id="finance-fee-provider"></select></label>
                <label class="field"><span>覆盖费率（BPS）</span><input id="finance-provider-bps" type="number" min="0" max="10000" step="1" /></label>
                <p class="field-help" id="finance-provider-bps-preview">—</p>
              </div>
            </div>
            <div class="editor-actions">
              <button class="secondary-button" id="finance-remove-provider-fee" type="button">恢复全局默认</button>
              <button class="secondary-button" id="finance-save-provider-fee" type="button">保存服务商费率</button>
              <button class="primary-button" type="submit"><span>保存全局默认</span><b>→</b></button>
            </div>
            <p class="form-status" id="finance-fee-status" hidden></p>
          </form>
        </div>
      </section>

      <dialog class="finance-detail-dialog" id="prepaid-detail-dialog" aria-labelledby="prepaid-detail-title">
        <div class="finance-detail-panel">
          <div class="panel-head"><strong id="prepaid-detail-title">充值详情</strong><button id="prepaid-detail-close" type="button">关闭</button></div>
          <dl id="prepaid-detail-content"></dl>
        </div>
      </dialog>
    </section>
  `);

  let overview = emptyOverview;
  let settings: CollectionSettings[] = [];
  let usageEvents: UsageEvent[] = [];
  let payables: ProviderPayable[] = [];
  let payouts: Payout[] = [];
  let prepaidOverview = emptyPrepaidOverview;
  let prepaidRecharges: PrepaidFinanceItem[] = [];
  let prepaidProducts: PrepaidFinanceOption[] = [];
  let prepaidProviders: PrepaidFinanceOption[] = [];
  let prepaidPagination: PrepaidFinancePagination = { page: 1, pageSize: 20, total: 0, totalPages: 0 };
  let reconciliation: Reconciliation = {
    settledOrdersMissingUsage: 0,
    usageMissingLedger: 0,
    usageMissingPayable: 0,
    payableMissingPayout: 0,
    paidPayoutMissingTransaction: 0,
  };
  let runtimeEnabled = false;
  let feeRules: FeeRuleOverview = { defaultRule: { id: "", scope: "DEFAULT", feeBps: 0, version: 0 }, providerRules: [], providers: [] };
  const financeTabs: FinanceTab[] = ["overview", "recharges", "settlements", "reconciliation", "settings"];
  const requestedTab = new URL(window.location.href).searchParams.get("financeTab") as FinanceTab | null;
  let activeTab: FinanceTab = requestedTab && financeTabs.includes(requestedTab) ? requestedTab : "overview";

  const setActiveTab = (tab: FinanceTab, updateURL = true): void => {
    activeTab = tab;
    document.querySelectorAll<HTMLButtonElement>("[data-finance-tab]").forEach(button => {
      const active = button.dataset.financeTab === tab;
      button.classList.toggle("active", active);
      button.setAttribute("aria-selected", String(active));
    });
    document.querySelectorAll<HTMLElement>("[data-finance-panel]").forEach(panel => {
      panel.hidden = panel.dataset.financePanel !== tab;
    });
    if (!updateURL) return;
    const url = new URL(window.location.href);
    if (tab === "overview") url.searchParams.delete("financeTab");
    else url.searchParams.set("financeTab", tab);
    window.history.replaceState(null, "", url);
  };

  const status = (message: string, error = false): void => {
    const element = document.querySelector<HTMLElement>("#finance-settings-status")!;
    element.hidden = false;
    element.className = `form-status ${error ? "error" : "success"}`;
    element.textContent = message;
  };

  const feeStatus = (message: string, error = false): void => {
    const element = document.querySelector<HTMLElement>("#finance-fee-status")!;
    element.hidden = false;
    element.className = `form-status ${error ? "error" : "success"}`;
    element.textContent = message;
  };

  const bpsLabel = (bps: number): string => `${bps} BPS = ${(bps / 100).toFixed(2)}%`;

  const renderSelectedProviderRule = (): void => {
    const providerID = (document.querySelector<HTMLSelectElement>("#finance-fee-provider")!).value;
    const override = feeRules.providerRules.find(rule => rule.providerId === providerID);
    (document.querySelector<HTMLInputElement>("#finance-provider-bps")!).value = String(override?.feeBps ?? feeRules.defaultRule.feeBps);
    document.querySelector("#finance-provider-bps-preview")!.textContent = override
      ? `${bpsLabel(override.feeBps)} · 单独配置 V${override.version}`
      : `${bpsLabel(feeRules.defaultRule.feeBps)} · 当前继承全局默认`;
    (document.querySelector<HTMLButtonElement>("#finance-remove-provider-fee")!).disabled = !override;
  };

  const render = (): void => {
    const money = (id: string, value: string): void => {
      document.querySelector(id)!.textContent = deps.formatUSDC(value);
    };
    document.querySelector("#prepaid-customer-count")!.textContent = String(prepaidOverview.customerCount);
    document.querySelector("#prepaid-recharge-count")!.textContent = String(prepaidOverview.rechargeCount);
    document.querySelector("#prepaid-provider-count")!.textContent = String(prepaidOverview.providerCount);
    money("#prepaid-recharge-total", prepaidOverview.rechargeTotalAtomic);
    money("#prepaid-available", prepaidOverview.availableBalanceAtomic);
    money("#prepaid-consumed", prepaidOverview.consumedAmountAtomic);
    money("#prepaid-fee", prepaidOverview.platformFeeRecognizedAtomic);
    money("#prepaid-provider-paid", prepaidOverview.providerPaidAtomic);
    money("#prepaid-provider-outstanding", prepaidOverview.providerOutstandingAtomic);
    document.querySelector("#prepaid-refund-summary")!.textContent =
      `退款 ${deps.formatUSDC(prepaidOverview.refundedAmountAtomic)} USDC`;
    document.querySelector("#prepaid-recharge-table-summary")!.textContent =
      `共 ${prepaidPagination.total} 笔 · ${prepaidOverview.customerCount} 位客户`;
    document.querySelector("#finance-updated-at")!.textContent =
      `更新于 ${new Date().toLocaleTimeString("zh-CN", { hour12: false })}`;
    if (!document.querySelector<HTMLElement>("#finance-view")!.hidden) {
      document.querySelector("#summary-count")!.textContent =
        `${deps.formatUSDC(prepaidOverview.rechargeTotalAtomic)} USDC 累计充值`;
    }

    const productFilter = document.querySelector<HTMLSelectElement>("#prepaid-product-filter")!;
    const selectedProduct = productFilter.value;
    productFilter.innerHTML = `<option value="">全部产品</option>${prepaidProducts.map(item =>
      `<option value="${deps.escapeHTML(item.id)}">${deps.escapeHTML(item.name)}</option>`
    ).join("")}`;
    productFilter.value = prepaidProducts.some(item => item.id === selectedProduct) ? selectedProduct : "";
    const providerFilter = document.querySelector<HTMLSelectElement>("#prepaid-provider-filter")!;
    const selectedProvider = providerFilter.value;
    providerFilter.innerHTML = `<option value="">全部 Provider</option>${prepaidProviders.map(item =>
      `<option value="${deps.escapeHTML(item.id)}">${deps.escapeHTML(item.name)}</option>`
    ).join("")}`;
    providerFilter.value = prepaidProviders.some(item => item.id === selectedProvider) ? selectedProvider : "";

    const settlementLabels: Record<PrepaidFinanceItem["settlementStatus"], string> = {
      NOT_DUE: "尚未产生应结",
      PENDING: "待结算",
      PARTIAL: "部分结算",
      SETTLED: "已结算",
    };
    document.querySelector<HTMLElement>("#prepaid-finance-empty")!.hidden = prepaidRecharges.length > 0;
    document.querySelector("#prepaid-finance-rows")!.innerHTML = prepaidRecharges.map(item => {
      const settlementClass = item.settlementStatus === "SETTLED"
        ? "published"
        : item.exceptionPayoutCount > 0 ? "archived" : "";
      const payoutStatus = item.exceptionPayoutCount > 0
        ? `${item.exceptionPayoutCount} 笔异常`
        : item.pendingPayoutCount > 0 ? `${item.pendingPayoutCount} 笔处理中` : `${item.paidPayoutCount}/${item.payoutCount} 笔已付`;
      return `
        <tr>
          <td data-label="客户"><strong title="${deps.escapeHTML(item.payerWallet)}">${deps.escapeHTML(deps.shortAddress(item.payerWallet))}</strong><small>${deps.escapeHTML(new Date(item.createdAt).toLocaleString("zh-CN"))}</small></td>
          <td data-label="产品"><strong>${deps.escapeHTML(item.productName)}</strong></td>
          <td data-label="充值 / 剩余"><strong>${deps.escapeHTML(deps.formatUSDC(item.rechargeAmountAtomic))} USDC</strong><small>余额 ${deps.escapeHTML(deps.formatUSDC(item.availableBalanceAtomic))} · ${item.quantityRemaining}/${item.quantityTotal} 次</small></td>
          <td data-label="Provider"><strong>${deps.escapeHTML(item.providerName)}</strong><small title="${deps.escapeHTML(item.providerWallet)}">${deps.escapeHTML(deps.shortAddress(item.providerWallet))}</small></td>
          <td data-label="应结 / 已结"><strong>${deps.escapeHTML(deps.formatUSDC(item.providerPayableRecognizedAtomic))} / ${deps.escapeHTML(deps.formatUSDC(item.providerPaidAtomic))} USDC</strong><small>待结 ${deps.escapeHTML(deps.formatUSDC(item.providerOutstandingAtomic))} USDC</small></td>
          <td data-label="状态"><span class="status-chip ${settlementClass}">${deps.escapeHTML(settlementLabels[item.settlementStatus])}</span><small>${deps.escapeHTML(payoutStatus)}${item.refundStatus ? ` · 退款 ${deps.escapeHTML(item.refundStatus)}` : ""}</small></td>
          <td data-label="详情" class="row-actions"><button type="button" data-prepaid-detail="${deps.escapeHTML(item.grantId)}">查看详情</button></td>
        </tr>`;
    }).join("");
    const prepaidTotalPages = Math.max(1, prepaidPagination.totalPages);
    document.querySelector("#prepaid-page-label")!.textContent =
      `第 ${prepaidPagination.page} / ${prepaidTotalPages} 页 · 共 ${prepaidPagination.total} 笔`;
    (document.querySelector<HTMLButtonElement>("#prepaid-prev-page")!).disabled = prepaidPagination.page <= 1;
    (document.querySelector<HTMLButtonElement>("#prepaid-next-page")!).disabled =
      prepaidPagination.totalPages === 0 || prepaidPagination.page >= prepaidPagination.totalPages;

    money("#finance-collected", overview.collectedAtomic);
    money("#finance-prepayment", overview.customerPrepaymentAtomic);
    money("#finance-fee", overview.platformFeeAtomic);
    money("#finance-payable", overview.providerPayableAtomic);
    money("#finance-paid", overview.providerPaidAtomic);
    document.querySelector("#finance-unknown")!.textContent = String(overview.unknownPayouts);

    const latest = settings[0];
    document.querySelector("#finance-config-state")!.textContent = runtimeEnabled
      ? latest ? `RUNTIME ON · V${latest.version} ${latest.enabled ? "ACTIVE" : "DISABLED"}` : "RUNTIME ON · NOT CONFIGURED"
      : "RUNTIME FLAG OFF";
    if (latest) {
      (document.querySelector<HTMLInputElement>("#finance-enabled")!).checked = latest.enabled;
      (document.querySelector<HTMLInputElement>("#finance-network")!).value = latest.network;
      (document.querySelector<HTMLInputElement>("#finance-token")!).value = latest.tokenAddress;
      (document.querySelector<HTMLInputElement>("#finance-receiver")!).value = latest.receiverWallet;
    }

    (document.querySelector<HTMLInputElement>("#finance-default-bps")!).value = String(feeRules.defaultRule.feeBps);
    document.querySelector("#finance-default-bps-preview")!.textContent = `${bpsLabel(feeRules.defaultRule.feeBps)} · V${feeRules.defaultRule.version}`;
    const providerSelect = document.querySelector<HTMLSelectElement>("#finance-fee-provider")!;
    const previousProvider = providerSelect.value;
    providerSelect.innerHTML = feeRules.providers.map(provider => `<option value="${deps.escapeHTML(provider.id)}">${deps.escapeHTML(provider.name)} · ${deps.escapeHTML(provider.slug)}</option>`).join("");
    if (feeRules.providers.some(provider => provider.id === previousProvider)) providerSelect.value = previousProvider;
    renderSelectedProviderRule();

    const checks: Array<[string, number]> = [
      ["已结算订单缺 Usage", reconciliation.settledOrdersMissingUsage],
      ["Usage 缺账务分录", reconciliation.usageMissingLedger],
      ["Usage 缺 Provider 应付", reconciliation.usageMissingPayable],
      ["Provider 应付缺 Payout", reconciliation.payableMissingPayout],
      ["已付 Payout 缺交易哈希", reconciliation.paidPayoutMissingTransaction],
    ];
    const reconciliationIssueCount = checks.reduce((total, [, count]) => total + count, 0);
    const payoutExceptionCount = prepaidRecharges.reduce((total, item) => total + item.exceptionPayoutCount, 0);
    const alert = document.querySelector<HTMLElement>("#finance-alert")!;
    const totalIssues = reconciliationIssueCount + overview.unknownPayouts + payoutExceptionCount;
    alert.hidden = totalIssues === 0;
    alert.textContent = totalIssues === 0
      ? ""
      : `发现 ${totalIssues} 项需要处理的账务或出款异常，请进入“账务对账”查看。`;
    document.querySelector("#finance-reconciliation-list")!.innerHTML = checks.map(([label, count]) => `
      <div><span>${deps.escapeHTML(label)}</span><strong class="${count === 0 ? "ok" : "bad"}">${count}</strong></div>
    `).join("");

    document.querySelector<HTMLElement>("#finance-usage-empty")!.hidden = usageEvents.length > 0;
    document.querySelector("#finance-usage-rows")!.innerHTML = usageEvents.map(item => `
      <tr>
        <td><strong>${deps.escapeHTML(item.id)}</strong><small>${deps.escapeHTML(item.orderId)} · ${deps.escapeHTML(item.paymentMode)} · ${deps.escapeHTML(new Date(item.createdAt).toLocaleString("zh-CN"))}</small></td>
        <td><strong>${deps.escapeHTML(deps.formatUSDC(item.buyerGrossAmountAtomic))} USDC</strong></td>
        <td><strong>${deps.escapeHTML(deps.formatUSDC(item.platformFeeAmountAtomic))} USDC</strong></td>
        <td><strong>${deps.escapeHTML(deps.formatUSDC(item.providerPayableAmountAtomic))} USDC</strong></td>
        <td><span class="status-chip ${item.fundingState === "AVAILABLE" ? "published" : ""}">${deps.escapeHTML(item.fundingState)}</span></td>
      </tr>
    `).join("");

    const payoutByPayable = new Map(payouts.map(item => [item.providerPayableId, item]));
    document.querySelector<HTMLElement>("#finance-payout-empty")!.hidden = payables.length > 0;
    const payoutRows = document.querySelector<HTMLElement>("#finance-payout-rows")!;
    payoutRows.innerHTML = payables.map(item => {
      const payout = payoutByPayable.get(item.id);
      return `
        <tr>
          <td data-label="Provider"><strong>${deps.escapeHTML(item.providerRef)}</strong><small title="${deps.escapeHTML(item.providerWallet)}">${deps.escapeHTML(deps.shortAddress(item.providerWallet))}</small></td>
          <td data-label="应付"><strong>${deps.escapeHTML(deps.formatUSDC(item.amountAtomic))} USDC</strong><small>已付 ${deps.escapeHTML(deps.formatUSDC(item.paidAmountAtomic))}</small></td>
          <td data-label="资金 / 应付状态"><span class="status-chip">${deps.escapeHTML(item.fundingState)} / ${deps.escapeHTML(item.status)}</span></td>
          <td data-label="出款状态"><span class="status-chip ${payout?.status === "PAID" ? "published" : payout?.status === "FAILED" || payout?.status === "UNKNOWN" ? "archived" : ""}">${deps.escapeHTML(payout?.status || "MISSING")}</span><small>${deps.escapeHTML(payout?.lastErrorCode || "")}</small></td>
          <td data-label="链上交易"><code title="${deps.escapeHTML(payout?.transactionHash || "")}">${deps.escapeHTML(payout?.transactionHash ? deps.shortAddress(payout.transactionHash) : "—")}</code></td>
          <td data-label="操作" class="row-actions">${payout?.status === "FAILED" ? `<button type="button" data-retry-payout="${deps.escapeHTML(payout.id)}">确认重试</button>` : ""}</td>
        </tr>`;
    }).join("");
    payoutRows.querySelectorAll<HTMLButtonElement>("[data-retry-payout]").forEach(button => {
      button.addEventListener("click", async () => {
        if (!window.confirm("只应重试已确认未出款的失败项。继续？")) return;
        button.disabled = true;
        try {
          await deps.api(`/v1/admin/finance/payouts/${button.dataset.retryPayout}/retry`, { method: "POST" });
          await load();
        } catch (error) {
          window.alert(error instanceof Error ? error.message : "重试失败");
        } finally {
          button.disabled = false;
        }
      });
    });
  };

  const openPrepaidDetail = (grantID: string): void => {
    const item = prepaidRecharges.find(recharge => recharge.grantId === grantID);
    if (!item) return;
    const payoutHash = item.latestPayoutTransactionHash || "—";
    const refund = item.refundStatus
      ? `${item.refundStatus} · ${deps.formatUSDC(item.refundAmountAtomic)} USDC · ${item.refundTransactionHash || "无交易哈希"}`
      : "无退款";
    const fields: Array<[string, string]> = [
      ["客户钱包", item.payerWallet],
      ["充值时间", new Date(item.createdAt).toLocaleString("zh-CN")],
      ["产品", item.productName],
      ["充值记录", item.grantId],
      ["订阅", item.subscriptionId],
      ["订单", item.orderId],
      ["Provider", `${item.providerName} · ${item.providerId}`],
      ["Provider 钱包", item.providerWallet],
      ["充值 / 可用", `${deps.formatUSDC(item.rechargeAmountAtomic)} / ${deps.formatUSDC(item.availableBalanceAtomic)} USDC`],
      ["额度", `${item.quantityRemaining} / ${item.quantityTotal} 次`],
      ["消费 / 平台费", `${deps.formatUSDC(item.consumedAmountAtomic)} / ${deps.formatUSDC(item.platformFeeRecognizedAtomic)} USDC · ${item.platformFeeBps} BPS`],
      ["Provider 应结 / 已结 / 待结", `${deps.formatUSDC(item.providerPayableRecognizedAtomic)} / ${deps.formatUSDC(item.providerPaidAtomic)} / ${deps.formatUSDC(item.providerOutstandingAtomic)} USDC`],
      ["充值交易", item.incomingTransactionHash || "—"],
      ["最新出款交易", payoutHash],
      ["退款", refund],
    ];
    document.querySelector("#prepaid-detail-content")!.innerHTML = fields.map(([label, value]) => `
      <div><dt>${deps.escapeHTML(label)}</dt><dd>${deps.escapeHTML(value)}</dd></div>
    `).join("");
    document.querySelector<HTMLDialogElement>("#prepaid-detail-dialog")!.showModal();
  };

  const prepaidFinancePath = (page: number): string => {
    const query = new URLSearchParams({
      page: String(page),
      pageSize: document.querySelector<HTMLSelectElement>("#prepaid-page-size")!.value,
    });
    const productID = document.querySelector<HTMLSelectElement>("#prepaid-product-filter")!.value;
    const providerID = document.querySelector<HTMLSelectElement>("#prepaid-provider-filter")!.value;
    const wallet = document.querySelector<HTMLInputElement>("#prepaid-wallet-filter")!.value.trim();
    if (productID) query.set("productId", productID);
    if (providerID) query.set("providerId", providerID);
    if (wallet) query.set("wallet", wallet);
    return `/v1/admin/finance/prepaid-entitlements?${query}`;
  };

  const applyPrepaidFinance = (body: PrepaidFinanceResponse): void => {
    prepaidOverview = body.overview;
    prepaidRecharges = body.recharges;
    prepaidPagination = body.pagination;
    prepaidProducts = body.products;
    prepaidProviders = body.providers;
  };

  const loadPrepaidFinance = async (page = prepaidPagination.page): Promise<void> => {
    applyPrepaidFinance(await deps.api<PrepaidFinanceResponse>(prepaidFinancePath(page)));
    render();
  };

  const load = async (): Promise<void> => {
    const [overviewBody, prepaidBody, settingsBody, feeRuleBody, usageBody, payableBody, payoutBody, reconciliationBody] = await Promise.all([
      deps.api<{ overview: FinanceOverview }>("/v1/admin/finance/overview"),
      deps.api<PrepaidFinanceResponse>(prepaidFinancePath(1)),
      deps.api<{ unifiedCollectionEnabled: boolean; settings: CollectionSettings[] }>("/v1/admin/finance/collection-settings"),
      deps.api<{ feeRules: FeeRuleOverview }>("/v1/admin/finance/fee-rules"),
      deps.api<{ usageEvents: UsageEvent[] }>("/v1/admin/finance/usage-events?page=1&pageSize=50"),
      deps.api<{ providerPayables: ProviderPayable[] }>("/v1/admin/finance/provider-payables?page=1&pageSize=50"),
      deps.api<{ payouts: Payout[] }>("/v1/admin/finance/payouts?page=1&pageSize=50"),
      deps.api<{ reconciliation: Reconciliation }>("/v1/admin/finance/reconciliation"),
    ]);
    overview = overviewBody.overview;
    applyPrepaidFinance(prepaidBody);
    settings = settingsBody.settings;
    runtimeEnabled = settingsBody.unifiedCollectionEnabled;
    feeRules = feeRuleBody.feeRules;
    usageEvents = usageBody.usageEvents;
    payables = payableBody.providerPayables;
    payouts = payoutBody.payouts;
    reconciliation = reconciliationBody.reconciliation;
    render();
  };

  document.querySelector<HTMLFormElement>("#finance-settings-form")!.addEventListener("submit", async event => {
    event.preventDefault();
    try {
      await deps.api("/v1/admin/finance/collection-settings", {
        method: "PUT",
        body: JSON.stringify({
          enabled: (document.querySelector<HTMLInputElement>("#finance-enabled")!).checked,
          network: (document.querySelector<HTMLInputElement>("#finance-network")!).value.trim(),
          tokenAddress: (document.querySelector<HTMLInputElement>("#finance-token")!).value.trim(),
          receiverWallet: (document.querySelector<HTMLInputElement>("#finance-receiver")!).value.trim(),
        }),
      });
      status("收款配置新版本已创建；运行时 Feature Flag 不会由页面自动打开。");
      await load();
    } catch (error) {
      status(error instanceof Error ? error.message : "保存收款配置失败", true);
    }
  });
  document.querySelector("#finance-refresh")!.addEventListener("click", () => void load());
  document.querySelector<HTMLFormElement>("#prepaid-finance-filters")!.addEventListener("submit", event => {
    event.preventDefault();
    void loadPrepaidFinance(1);
  });
  document.querySelector("#prepaid-prev-page")!.addEventListener("click", () => {
    if (prepaidPagination.page > 1) void loadPrepaidFinance(prepaidPagination.page - 1);
  });
  document.querySelector("#prepaid-next-page")!.addEventListener("click", () => {
    if (prepaidPagination.page < prepaidPagination.totalPages) void loadPrepaidFinance(prepaidPagination.page + 1);
  });
  document.querySelector("#prepaid-finance-rows")!.addEventListener("click", event => {
    const button = (event.target as Element).closest<HTMLButtonElement>("[data-prepaid-detail]");
    if (button?.dataset.prepaidDetail) openPrepaidDetail(button.dataset.prepaidDetail);
  });
  document.querySelectorAll<HTMLButtonElement>("[data-finance-tab]").forEach(button => {
    button.addEventListener("click", () => setActiveTab(button.dataset.financeTab as FinanceTab));
  });
  const detailDialog = document.querySelector<HTMLDialogElement>("#prepaid-detail-dialog")!;
  document.querySelector("#prepaid-detail-close")!.addEventListener("click", () => detailDialog.close());
  detailDialog.addEventListener("click", event => {
    if (event.target === detailDialog) detailDialog.close();
  });
  document.querySelector("#finance-fee-provider")!.addEventListener("change", renderSelectedProviderRule);
  document.querySelector<HTMLInputElement>("#finance-provider-bps")!.addEventListener("input", event => {
    const bps = Number((event.currentTarget as HTMLInputElement).value || 0);
    document.querySelector("#finance-provider-bps-preview")!.textContent = bpsLabel(bps);
  });
  document.querySelector<HTMLFormElement>("#finance-fee-form")!.addEventListener("submit", async event => {
    event.preventDefault();
    try {
      const feeBps = Number((document.querySelector<HTMLInputElement>("#finance-default-bps")!).value);
      await deps.api("/v1/admin/finance/fee-rules/default", { method: "PUT", body: JSON.stringify({ feeBps }) });
      feeStatus(`全局默认费率已更新为 ${bpsLabel(feeBps)}。新报价立即使用，历史报价不变。`);
      await load();
    } catch (error) {
      feeStatus(error instanceof Error ? error.message : "保存全局费率失败", true);
    }
  });
  document.querySelector("#finance-save-provider-fee")!.addEventListener("click", async () => {
    const providerID = (document.querySelector<HTMLSelectElement>("#finance-fee-provider")!).value;
    const feeBps = Number((document.querySelector<HTMLInputElement>("#finance-provider-bps")!).value);
    if (!providerID) return;
    try {
      await deps.api(`/v1/admin/finance/fee-rules/providers/${encodeURIComponent(providerID)}`, { method: "PUT", body: JSON.stringify({ feeBps }) });
      feeStatus(`服务商单独费率已更新为 ${bpsLabel(feeBps)}。`);
      await load();
    } catch (error) {
      feeStatus(error instanceof Error ? error.message : "保存服务商费率失败", true);
    }
  });
  document.querySelector("#finance-remove-provider-fee")!.addEventListener("click", async () => {
    const providerID = (document.querySelector<HTMLSelectElement>("#finance-fee-provider")!).value;
    if (!providerID) return;
    try {
      await deps.api(`/v1/admin/finance/fee-rules/providers/${encodeURIComponent(providerID)}`, { method: "DELETE" });
      feeStatus("服务商单独费率已删除，现继承全局默认。");
      await load();
    } catch (error) {
      feeStatus(error instanceof Error ? error.message : "删除服务商费率失败", true);
    }
  });
  setActiveTab(activeTab, false);

  return {
    load,
    summary: () => `${deps.formatUSDC(prepaidOverview.rechargeTotalAtomic)} USDC 累计充值`,
  };
}
